import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { type LoginResponse, fetchMe } from './api/auth'
import { initCsrf } from './api/client'
import { CircularProgress } from './components/ui'
import { LoginPage } from './pages/LoginPage'
import { SsoPage } from './pages/SsoPage'
import { MfaSetupGatePage } from './pages/MfaSetupGatePage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { CustomerLinkPage } from './pages/public/CustomerLinkPage'
import { SignaturePage } from './pages/public/SignaturePage'
import { ReschedulePage } from './pages/public/ReschedulePage'
import { Dashboard } from './pages/Dashboard'
import { LeadsPage } from './pages/LeadsPage'
import { LeadDetailPage } from './pages/LeadDetailPage'
import { DealsPage } from './pages/DealsPage'
import { DealDetailPage } from './pages/DealDetailPage'
import { RecontactsPage } from './pages/RecontactsPage'
import { LeadFormPage } from './pages/LeadFormPage'
import { ContractsPage } from './pages/ContractsPage'
import { CustomersPage } from './pages/CustomersPage'
import { PipelinesPage } from './pages/PipelinesPage'
import { ProductsPage } from './pages/ProductsPage'
import { FinancePage } from './pages/FinancePage'
import { ExpensesPage } from './pages/ExpensesPage'
import { SupplierInvoicesPage } from './pages/SupplierInvoicesPage'
import { PartnersPage } from './pages/PartnersPage'
import { CostControlPage } from './pages/CostControlPage'
import { AccountantPage } from './pages/AccountantPage'
import { BankPage } from './pages/BankPage'
import { FinanceControlsPage } from './pages/FinanceControlsPage'
import { ApprovalMatrixPage } from './pages/ApprovalMatrixPage'
import { AnnualArchivePage } from './pages/AnnualArchivePage'
import { CredentialVaultPage } from './pages/CredentialVaultPage'
import { PaymentCalendarPage } from './pages/PaymentCalendarPage'
import { ReceivablesPage } from './pages/ReceivablesPage'
import { AdminCockpitPage } from './pages/AdminCockpitPage'
import { PracticePaymentsQueuePage } from './pages/PracticePaymentsQueuePage'
import { IncomeStatementPage } from './pages/IncomeStatementPage'
import { OperationsPage } from './pages/OperationsPage'
import { SettingsPage } from './pages/SettingsPage'
import { ValidationRulesPage } from './pages/ValidationRulesPage'
import { DynamicFieldsPage } from './pages/DynamicFieldsPage'
import { AutomationRulesPage } from './pages/AutomationRulesPage'
import { ConfigurationTemplatesPage } from './pages/ConfigurationTemplatesPage'
import { CustomerDetailPage } from './pages/CustomerDetailPage'
import { ContractDetailPage } from './pages/ContractDetailPage'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { WorkstationPage } from './pages/WorkstationPage'
import { WorkQueuePage } from './pages/WorkQueuePage'
import { CallCenterManagerPage } from './pages/CallCenterManagerPage'
import { UsersPage } from './pages/UsersPage'
import { UserCategoriesPage } from './pages/UserCategoriesPage'
import { LeadRoutingRulesPage } from './pages/LeadRoutingRulesPage'
import { LeadRulesPage } from './pages/LeadRulesPage'
import { DepartmentsPage } from './pages/DepartmentsPage'
import { PaymentsPage } from './pages/PaymentsPage'
import { CommissionsPage } from './pages/CommissionsPage'
import { CallOutcomesPage } from './pages/CallOutcomesPage'
import { CallCenterSetupPage } from './pages/CallCenterSetupPage'
import { CommissionPlanRatesPage } from './pages/CommissionPlanRatesPage'
import { CommissionAssignmentsPage } from './pages/CommissionAssignmentsPage'
import { ActivityPayoutsPage } from './pages/ActivityPayoutsPage'
import { TeamPage } from './pages/TeamPage'
import { ReportsPage } from './pages/ReportsPage'
import { TargetsPage } from './pages/TargetsPage'
import { ContestsPage } from './pages/ContestsPage'
import { ContestDetailPage } from './pages/ContestDetailPage'
import { CampaignsPage } from './pages/CampaignsPage'
import { AuditPage } from './pages/AuditPage'
import { OutboxPage } from './pages/OutboxPage'
import { IntegrationsPage } from './pages/IntegrationsPage'
import { JobsPage } from './pages/JobsPage'
import { PlatformHealthPage } from './pages/PlatformHealthPage'
import { ApiDocsPage } from './pages/ApiDocsPage'
import { ImplementationStatusPage } from './pages/ImplementationStatusPage'
import { FeatureFlagsPage } from './pages/FeatureFlagsPage'
import { CustomerFormPage } from './pages/CustomerFormPage'
import { ContractFormPage } from './pages/ContractFormPage'
import { CompaniesPage } from './pages/CompaniesPage'
import { CompanyFormPage } from './pages/CompanyFormPage'
import { ContactFormPage } from './pages/ContactFormPage'
import { PersonFormPage } from './pages/PersonFormPage'
import { CompanyDetailPage } from './pages/CompanyDetailPage'
import { ContactDetailPage } from './pages/ContactDetailPage'
import { NextBestActionsPage } from './pages/NextBestActionsPage'
import { NextBestActionRulesPage } from './pages/NextBestActionRulesPage'
import { ProductPenetrationPage } from './pages/ProductPenetrationPage'
import { ApprovalsPage } from './pages/ApprovalsPage'
import { BatchesPage } from './pages/BatchesPage'
import { FunnelsPage } from './pages/FunnelsPage'
import { LeadGenerationReportPage } from './pages/LeadGenerationReportPage'
import { MarketingEconomicsPage } from './pages/MarketingEconomicsPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { LibraryPage } from './pages/LibraryPage'
import { SupplyPointTypesPage } from './pages/SupplyPointTypesPage'
import { FormsPage } from './pages/FormsPage'
import { FormDetailPage } from './pages/FormDetailPage'
import { DocumentTemplatesPage } from './pages/DocumentTemplatesPage'
import { BusinessEventsPage } from './pages/BusinessEventsPage'
import { PeriodComparisonPage } from './pages/PeriodComparisonPage'
import { LeadProvidersPage } from './pages/LeadProvidersPage'
import { BackofficeDashboardPage } from './pages/BackofficeDashboardPage'
import { BackofficeQueuePage } from './pages/BackofficeQueuePage'
import { OutcomeImportsPage } from './pages/OutcomeImportsPage'
import { AccountSecurityPage } from './pages/AccountSecurityPage'
import { MyAgentProfilePage } from './pages/MyAgentProfilePage'
import { AgentBankRequestsPage } from './pages/AgentBankRequestsPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { ImportsPage } from './pages/ImportsPage'
import { ProcessMiningPage } from './pages/ProcessMiningPage'
import { MyDashboardPage } from './pages/MyDashboardPage'
import { HomeLanding, HomeTilePage, RoleHomePage } from './pages/RoleHomePage'
import { EqualPointPage } from './pages/EqualPointPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { ProjectDetailPage } from './pages/ProjectDetailPage'
import { ProjectProposalsPage } from './pages/projectProposals/ProjectProposalsPage'
import { ProjectProposalPage } from './pages/projectProposals/ProjectProposalPage'
import { DomainsPage } from './pages/DomainsPage'
import { DomainDetailPage } from './pages/DomainDetailPage'
import { ADMIN_ROLES } from './navigation/roles'
import { ProductivityReportPage } from './pages/ProductivityReportPage'
import { RevenueReportPage } from './pages/reports/RevenueReportPage'
import { QualityReportPage } from './pages/reports/QualityReportPage'
import { BackofficeReportPage } from './pages/reports/BackofficeReportPage'
import { SectorsReportPage } from './pages/reports/SectorsReportPage'
import { AgentRankingReportPage } from './pages/reports/AgentRankingReportPage'
import { ExecutiveCockpitPage } from './pages/ExecutiveCockpitPage'
import { CatalogGovernancePage } from './pages/CatalogGovernancePage'
import { ProductDetailPage } from './pages/ProductDetailPage'
import { DataQualityPage } from './pages/DataQualityPage'
import { ConfigurationCheckPage } from './pages/ConfigurationCheckPage'
import { TrashPage } from './pages/TrashPage'
import { LeadStatusesPage } from './pages/LeadStatusesPage'
import { LeadSourcesPage } from './pages/LeadSourcesPage'
import { LeadInboxPage } from './pages/LeadInboxPage'
import { ChangeReasonDialog } from './components/ChangeReasonDialog'
import { AppShell } from './layout/AppShell'

/* ── Auth Layout ── */
// Card B (design M3): sidebar, topbar e pannello tema vivono in src/layout (AppShell) e
// src/components/ui/navigation; qui restano le rotte.
function AuthLayout({ auth, onLogout }: { auth: LoginResponse; onLogout: () => void }) {
  return (
    <>
      <AppShell auth={auth} onLogout={onLogout}>
        <div className="md-content">
          <Routes>
            <Route path="/dashboard" element={<Dashboard auth={auth} onLogout={onLogout} />} />
            <Route path="/home" element={<RoleHomePage />} />
            <Route path="/equal-point" element={<EqualPointPage fallback={auth.role === 'EqualBackOffice' ? '/backoffice/queue' : '/dashboard'} />} />
            <Route path="/home/:key" element={<HomeTilePage />} />
            <Route path="/my-dashboard" element={<MyDashboardPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/leads/recontacts" element={<RecontactsPage />} />
            <Route path="/leads/inbox" element={<LeadInboxPage />} />
            <Route path="/leads/create" element={<LeadFormPage mode="create" />} />
            <Route path="/leads/:id" element={<LeadDetailPage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/new" element={<ProjectDetailPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage canDelete={ADMIN_ROLES.includes(auth.role)} />} />
            <Route path="/project-proposals" element={<ProjectProposalsPage />} />
            <Route path="/project-proposals/new" element={<ProjectProposalPage />} />
            <Route path="/project-proposals/:id" element={<ProjectProposalPage />} />
            <Route path="/domains" element={<DomainsPage />} />
            <Route path="/domains/new" element={<DomainDetailPage />} />
            <Route path="/domains/:id" element={<DomainDetailPage canDelete={ADMIN_ROLES.includes(auth.role)} />} />
            <Route path="/deals/new" element={<DealDetailPage />} />
            <Route path="/deals/:id" element={<DealDetailPage />} />
            <Route path="/leads/:id/edit" element={<LeadFormPage mode="edit" />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/create" element={<CustomerFormPage mode="create" />} />
            <Route path="/customers/:id/edit" element={<CustomerFormPage mode="edit" />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            {/* F-068 (TASK-0322): importazioni di lead e clienti (lotti, mappature, annullamento). */}
            <Route path="/imports" element={<ImportsPage auth={auth} />} />
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/contracts/create" element={<ContractFormPage />} />
            {/* TASK-0269 (F-015) */}
            <Route path="/backoffice" element={<BackofficeDashboardPage />} />
            {/* F-018 (REQ-038-004/005): UI import esiti operatore e coda riconciliazione */}
            <Route path="/backoffice/outcome-imports" element={<OutcomeImportsPage />} />
            {/* F-089 (TASK-0343): coda del backoffice (eccezioni, SLA, regole di priorita'). */}
            <Route path="/backoffice/queue" element={<BackofficeQueuePage auth={auth} />} />
            <Route path="/contracts/:id" element={<ContractDetailPage />} />
            <Route path="/pipelines" element={<PipelinesPage />} />
            <Route path="/products" element={<ProductsPage />} />
            {/* F-067 (TASK-0321): governance del catalogo — verifica, usi e versioni (?tipo=operatore per un operatore); nessuna voce di menu, si arriva da ProductsPage. */}
            {/* R3-12: scheda del prodotto con il tab Configurazione (dietro il flag pratiche.campi_condizionali). */}
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/products/:id/governance" element={<CatalogGovernancePage />} />
            <Route path="/finance" element={<FinancePage />} />
            {/* TASK-0276 (F-022): auth passato per gating client-side Admin (transizioni di stato)
                / EqualStaff (creazione/modifica spese e categorie) — la protezione reale resta
                server-side (ExpensesController/ExpenseCategoriesController), stesso schema di
                CompanyDetailPage/ContestDetailPage/FormDetailPage. */}
            <Route path="/finance/expenses" element={<ExpensesPage auth={auth} />} />
            {/* R5-07 (Z-17, Z-23): fatture fornitori da fatturazione + regole d'imposta. */}
            <Route path="/finance/supplier-invoices" element={<SupplierInvoicesPage auth={auth} />} />
            {/* F-059 (TASK-0313): conto corrente dei soci — il gating reale è server-side. */}
            <Route path="/finance/partners" element={<PartnersPage auth={auth} />} />
            {/* F-064 (TASK-0318): controllo costi — il gating reale è server-side (Admin). */}
            <Route path="/finance/costs" element={<CostControlPage auth={auth} />} />
            {/* F-069 (TASK-0323): export e chiusura periodo — il gating reale è server-side (FinanceAdministrationRead). */}
            <Route path="/finance/accountant" element={<AccountantPage auth={auth} />} />
            {/* F-077 (TASK-0331): banca e riconciliazione — il gating reale è server-side (FinanceAdministrationRead / FinanceAdministration). */}
            <Route path="/finance/bank" element={<BankPage auth={auth} />} />
            {/* F-079 (TASK-0333): matrice di approvazione delle spese, duplicati, alert e soglie dei soci — il gating reale è server-side (FinanceAdministrationRead / FinanceAdministration). */}
            <Route path="/finance/controls" element={<FinanceControlsPage auth={auth} />} />
            <Route path="/settings/approval-matrix" element={<ApprovalMatrixPage auth={auth} />} />
            <Route path="/settings/annual-archive" element={<AnnualArchivePage />} />
            <Route path="/settings/credentials" element={<CredentialVaultPage auth={auth} />} />
            {/* F-085 (TASK-0339): scadenziario unico e scadenze manuali — il gating reale è server-side (FinanceAdministrationRead / FinanceAdministration). */}
            <Route path="/finance/calendar" element={<PaymentCalendarPage auth={auth} />} />
            {/* Blocco 1 (Card D): crediti attesi, cruscotto amministrativo, conto economico — dietro feature flag, il gating reale è server-side (FinanceAdministrationRead / FinanceAdministration). */}
            <Route path="/finance/receivables" element={<ReceivablesPage auth={auth} />} />
            <Route path="/finance/cockpit" element={<AdminCockpitPage auth={auth} />} />
            <Route path="/finance/practice-payments" element={<PracticePaymentsQueuePage auth={auth} />} />
            <Route path="/finance/pnl" element={<IncomeStatementPage auth={auth} />} />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/users" element={<UsersPage />} />
            <Route path="/settings/user-categories" element={<UserCategoriesPage />} />
            <Route path="/settings/lead-routing-rules" element={<LeadRoutingRulesPage />} />
            <Route path="/settings/lead-providers" element={<LeadProvidersPage />} />
            <Route path="/settings/call-outcomes" element={<CallOutcomesPage />} />
            <Route path="/settings/call-center" element={<CallCenterSetupPage />} />
            <Route path="/settings/lead-rules" element={<LeadRulesPage />} />
            {/* F-102 (TASK-0356): il gating reale è server-side (Admin). */}
            <Route path="/settings/lead-statuses" element={<LeadStatusesPage auth={auth} />} />
            <Route path="/settings/lead-sources" element={<LeadSourcesPage auth={auth} />} />
            <Route path="/settings/validation-rules" element={<ValidationRulesPage auth={auth} />} />
            {/* F-074 (TASK-0328): il gating reale è server-side (Admin). */}
            <Route path="/settings/dynamic-fields" element={<DynamicFieldsPage auth={auth} />} />
            {/* F-111 (TASK-0365): tipi di punto di fornitura. Il gating reale è server-side (Admin). */}
            <Route path="/settings/supply-point-types" element={<SupplyPointTypesPage auth={auth} />} />
            <Route path="/settings/automations" element={<AutomationRulesPage />} />
            {/* R3-29: regole delle azioni consigliate. Il gating reale e' server-side (Admin, flag regole.ciclo_vita). */}
            <Route path="/settings/next-best-action-rules" element={<NextBestActionRulesPage />} />
            <Route path="/settings/document-templates" element={<DocumentTemplatesPage />} />
            {/* F-081 (TASK-0335): template di configurazione. Il gating reale è server-side (Admin). */}
            <Route path="/settings/configuration-templates" element={<ConfigurationTemplatesPage />} />
            <Route path="/departments" element={<DepartmentsPage canAssignBranch={ADMIN_ROLES.includes(auth.role)} />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/commissions" element={<CommissionsPage />} />
            <Route path="/commissions/plan-rates" element={<CommissionPlanRatesPage />} />
            <Route path="/commissions/assignments" element={<CommissionAssignmentsPage />} />
            <Route path="/commissions/activity-payouts" element={<ActivityPayoutsPage auth={auth} />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/process" element={<ProcessMiningPage />} />
            <Route path="/reports/executive" element={<ExecutiveCockpitPage />} />
            <Route path="/targets" element={<TargetsPage />} />
            <Route path="/contests" element={<ContestsPage />} />
            <Route path="/contests/:id" element={<ContestDetailPage auth={auth} />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/outbox" element={<OutboxPage />} />
            <Route path="/settings/integrations" element={<IntegrationsPage />} />
            <Route path="/settings/business-events" element={<BusinessEventsPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/platform-health" element={<PlatformHealthPage />} />
            <Route path="/platform/api" element={<ApiDocsPage />} />
            <Route path="/settings/implementation-status" element={<ImplementationStatusPage />} />
            <Route path="/settings/feature-flags" element={<FeatureFlagsPage />} />
            <Route path="/settings/data-quality" element={<DataQualityPage />} />
            {/* F-122 (TASK-0376): il gating reale è server-side (Admin). */}
            <Route path="/settings/configuration-check" element={<ConfigurationCheckPage />} />
            <Route path="/settings/trash" element={<TrashPage />} />
            <Route path="/workstation" element={<WorkstationPage />} />
            <Route path="/work-queue" element={<WorkQueuePage />} />
            {/* F-084 (TASK-0338): cruscotto del responsabile del call center (il server richiede la policy Manager). */}
            <Route path="/call-center" element={<CallCenterManagerPage />} />
            {/* F-076 (TASK-0330): prossime azioni consigliate sui miei clienti. */}
            <Route path="/next-actions" element={<NextBestActionsPage />} />
            <Route path="/executive" element={<PeriodComparisonPage />} />
            <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/companies/create" element={<CompanyFormPage mode="create" />} />
            <Route path="/companies/:id/edit" element={<CompanyFormPage mode="edit" />} />
            <Route path="/companies/:id" element={<CompanyDetailPage auth={auth} />} />
            {/* TASK-0171: edit-only, reachable exclusively via the "Usa questo record esistente"
                duplicate-conflict button (Customer/Company forms) or a direct URL — deliberately
                no /contacts list or /contacts/create route, no sidebar entry. */}
            <Route path="/contacts/:id/edit" element={<ContactFormPage />} />
            {/* F-076 (TASK-0330): scheda cliente 360 (il cliente e' un contatto). */}
            <Route path="/contacts/:id" element={<ContactDetailPage auth={auth} />} />
            {/* TASK-0184: edit-only, reachable exclusively via the "Usa questo record esistente"
                duplicate-conflict button (Customer/Company forms) or a direct URL — deliberately
                no /people list or /people/create route, no sidebar entry. */}
            <Route path="/people/:id/edit" element={<PersonFormPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/forms" element={<FormsPage />} />
            <Route path="/forms/create" element={<FormDetailPage auth={auth} />} />
            <Route path="/forms/:id" element={<FormDetailPage auth={auth} />} />
            <Route path="/funnels" element={<FunnelsPage />} />
            {/* F-060 / REQ-094-001 */}
            <Route path="/reports/lead-generation" element={<LeadGenerationReportPage />} />
            <Route path="/reports/marketing-economics" element={<MarketingEconomicsPage />} />
            <Route path="/reports/productivity" element={<ProductivityReportPage />} />
            <Route path="/reports/dedicated/revenue" element={<RevenueReportPage />} />
            <Route path="/reports/dedicated/quality" element={<QualityReportPage />} />
            <Route path="/reports/dedicated/backoffice" element={<BackofficeReportPage />} />
            <Route path="/reports/dedicated/sectors" element={<SectorsReportPage />} />
            <Route path="/reports/dedicated/agents" element={<AgentRankingReportPage />} />
            {/* F-076 (TASK-0330): penetrazione prodotti, ruoli Manager (l'Admin vi gestisce anche le regole). */}
            <Route path="/reports/penetration" element={<ProductPenetrationPage auth={auth} />} />
            <Route path="/batches" element={<BatchesPage auth={auth} />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/tickets" element={<PlaceholderPage title="Ticket & Richieste" icon="support_agent" />} />
            {/* F-040 / REQ-032-002 */}
            <Route path="/account/security" element={<AccountSecurityPage />} />
            {/* R3-28 / REQ-260-001 */}
            <Route path="/account/agent-profile" element={<MyAgentProfilePage />} />
            <Route path="/settings/agent-bank-requests" element={<AgentBankRequestsPage />} />
            {/* F-043 / REQ-267-001 */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="*" element={<HomeLanding fallback={auth.role === 'EqualBackOffice' ? '/backoffice/queue' : '/dashboard'} />} />
          </Routes>
        </div>
      </AppShell>

      <ChangeReasonDialog />
    </>
  )
}

/**
 * TASK-0044: real session detection on mount, instead of always assuming logged-out. Calls
 * GET /api/auth/me (reads the current Redis-backed session, TASK-0037 — a genuine 8h sliding
 * idle timeout, not a client-side assumption) before App ever decides to show LoginPage. A 200
 * means the session is still valid and the caller should populate `auth` and skip straight to
 * the authenticated UI; a 401 (fetchMe already turns this into a plain `null`, not a throw) or
 * any other failure preserves the pre-existing behavior of showing LoginPage. Extracted as its
 * own function — rather than inlined in the mount effect — so the 200-vs-401 branch is a single
 * isolated async call, exercisable on its own (e.g. by a future frontend test harness — this repo
 * currently has none, and adding one is outside this task's allowed_paths). Verified here instead
 * via headless-Chrome visual verification: log in, hard-reload, confirm the user stays
 * authenticated; then log out, hard-reload, confirm LoginPage is shown (no false-positive
 * persistence).
 */
async function detectSession(): Promise<LoginResponse | null> {
  try {
    return await fetchMe()
  } catch {
    // Anything other than the expected 401 (already handled inside fetchMe): treat as "no
    // session" rather than let the mount effect throw — LoginPage is always a safe fallback.
    return null
  }
}

/* ── App root ── */
function App() {
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    // initCsrf() is caught individually: Promise.all rejects as soon as ANY of its inputs
    // rejects, which would skip the .then() below entirely — including a genuinely valid
    // detectSession() result — and silently discard it, showing LoginPage to a still-logged-in
    // user on the rare occasion initCsrf() hits a real network-level failure (not just a non-2xx
    // response, which it already swallows internally). detectSession() itself never rejects (see
    // its own try/catch), so this leaves Promise.all itself unable to reject.
    Promise.all([initCsrf().catch(() => undefined), detectSession()])
      .then(([, me]) => setAuth(me))
      .finally(() => setReady(true))
  }, [])

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100svh' }}>
        <CircularProgress label="Caricamento" />
      </div>
    )
  }

  return (
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "") || "/"}>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Equal PSW: ritorno dal vault (percorso registrato nel vault, non cambiarlo). */}
        <Route path="/sso" element={auth ? <Navigate to="/" replace /> : <SsoPage onLogin={setAuth} />} />
        {/* C-09: link sicuro per il cliente, fuori dal login (flag portale_cliente lato server). */}
        <Route path="/c/f/:token" element={<SignaturePage />} />
        <Route path="/c/f" element={<SignaturePage />} />
        <Route path="/c/:token" element={<CustomerLinkPage />} />
        <Route path="/c" element={<CustomerLinkPage />} />
        <Route path="/r/:token" element={<ReschedulePage />} />
        <Route path="/r" element={<ReschedulePage />} />
        <Route
          path="*"
          element={
            auth?.mfaSetupRequired
              ? <MfaSetupGatePage
                  auth={auth}
                  onDone={() => setAuth({ ...auth, mfaSetupRequired: false })}
                  onLogout={() => setAuth(null)} />
              : auth
              ? <AuthLayout auth={auth} onLogout={() => setAuth(null)} />
              : <LoginPage onLogin={setAuth} />
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default App
